import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Slide07Mastery: React.FC = () => {
  const [currentTier, setCurrentTier] = useState(0);

  const masteryTiers = [
    { label: 'Bronze', color: '#cd7f32', bgColor: 'rgba(205, 127, 50, 0.2)', reviewFreq: 'Daily review' },
    { label: 'Silver', color: '#c0c0c0', bgColor: 'rgba(192, 192, 192, 0.2)', reviewFreq: 'Weekly review' },
    { label: 'Gold', color: '#ffd700', bgColor: 'rgba(255, 215, 0, 0.2)', reviewFreq: 'Monthly review' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTier(prev => (prev + 1) % 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-4 text-center"
      >
        Mastery & Spaced Repetition
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-2xl text-[#94a3b8] mb-12 text-center max-w-4xl"
      >
        After completing a lesson, continued practice builds mastery. Higher mastery = less frequent review needed.
      </motion.p>

      {/* Flow diagram */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-5xl"
      >
        {/* Lesson Complete → Practice → Mastery Tiers */}
        <div className="flex items-center justify-center gap-6 mb-12">
          {/* Lesson Complete */}
          <div
            className="px-8 py-5 rounded-xl text-center"
            style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '2px solid #10b981',
            }}
          >
            <CheckCircleIcon className="w-10 h-10 text-[#10b981] mx-auto mb-2" />
            <div className="text-xl font-semibold text-white">Lesson Complete</div>
            <div className="text-sm text-[#94a3b8]">Student passes the lesson</div>
          </div>

          {/* Arrow */}
          <div className="text-4xl text-[#64748b]">→</div>

          {/* Keep Practicing */}
          <div
            className="px-8 py-5 rounded-xl text-center"
            style={{
              background: 'rgba(59, 130, 246, 0.2)',
              border: '2px solid #3b82f6',
            }}
          >
            <div className="text-4xl mb-2">🔄</div>
            <div className="text-xl font-semibold text-white">Keep Practicing</div>
            <div className="text-sm text-[#94a3b8]">Build deeper understanding</div>
          </div>

          {/* Arrow */}
          <div className="text-4xl text-[#64748b]">→</div>

          {/* Achieve Mastery */}
          <div
            className="px-8 py-5 rounded-xl text-center"
            style={{
              background: 'rgba(245, 158, 11, 0.2)',
              border: '2px solid #f59e0b',
            }}
          >
            <div className="text-4xl mb-2">🏆</div>
            <div className="text-xl font-semibold text-white">Achieve Mastery</div>
            <div className="text-sm text-[#94a3b8]">Bronze → Silver → Gold</div>
          </div>
        </div>

        {/* Mastery Tiers */}
        <div className="flex items-stretch justify-center gap-8">
          {masteryTiers.map((tier, idx) => (
            <motion.div
              key={tier.label}
              animate={{
                scale: idx === currentTier ? 1.05 : 1,
                boxShadow: idx === currentTier ? `0 0 30px ${tier.color}50` : 'none',
              }}
              transition={{ duration: 0.3 }}
              className="flex-1 max-w-[280px] rounded-xl p-6 text-center"
              style={{
                background: tier.bgColor,
                border: `3px solid ${tier.color}`,
              }}
            >
              <div
                className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-5xl"
                style={{ background: `${tier.color}30` }}
              >
                🥇
              </div>
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: tier.color }}
              >
                {tier.label}
              </div>
              <div className="text-lg text-white mb-3">Mastery</div>
              <div
                className="inline-block px-4 py-2 rounded-full text-sm font-medium"
                style={{ background: 'rgba(0,0,0,0.3)', color: tier.color }}
              >
                {tier.reviewFreq}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Explanation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-10 text-center"
        >
          <p className="text-xl text-[#94a3b8]">
            The AI schedules review questions based on mastery level —
            <span className="text-white font-medium"> less review for mastered topics, more for those needing reinforcement</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Slide07Mastery;
